import { getAuthoredDescriptor } from '../builder-v2/index.ts';
import {
  legacyDefinitionToComposite,
  trackTypeCompatibility,
  type ModularPieceDefinitionId,
  type TrackType,
} from '../editor/index.ts';
import type { CoasterProject, TrackBehavior } from '../schema/index.ts';

const TRACK_TYPES: readonly TrackType[] = ['normal', 'lift', 'launch', 'brake', 'photo'];
const MODULAR_DEFINITIONS = new Set<ModularPieceDefinitionId>([
  'station',
  'straight',
  'turn_left',
  'turn_right',
  'slope_up',
  'slope_down',
  'bank_left',
  'bank_right',
  'bank_level',
  'lift',
  'launch',
  'brake',
  'photo',
  'loop_left',
  'loop_right',
  'roll_left',
  'roll_right',
  'corkscrew_left',
  'corkscrew_right',
]);

function isModularDefinition(value: string): value is ModularPieceDefinitionId {
  return MODULAR_DEFINITIONS.has(value as ModularPieceDefinitionId);
}

export function allowedSectionBehaviorKinds(
  project: CoasterProject,
  sectionId: string,
): readonly TrackBehavior['kind'][] {
  if (project.track.geometryKind === 'authored_grid_v2') {
    const piece = project.track.pieces.find((candidate) =>
      candidate.sectionIds.includes(sectionId),
    );
    const descriptor = piece ? getAuthoredDescriptor(piece.descriptorId) : null;
    return descriptor?.allowedTrackTypes ?? ['normal'];
  }

  const piece = project.editor.pieceGroups.find((candidate) =>
    candidate.sectionIds.includes(sectionId),
  );
  if (!piece || piece.definitionId === 'custom' || !isModularDefinition(piece.definitionId))
    return ['normal'];
  if (piece.definitionId === 'station') return ['station'];
  if (!piece.entryPose) return ['normal'];

  const definition = piece.definitionId as ModularPieceDefinitionId;
  const { shapeId } = legacyDefinitionToComposite(definition);
  return TRACK_TYPES.filter(
    (trackType) =>
      trackTypeCompatibility(shapeId, piece.entryPose!, trackType, piece.parameters ?? {}).allowed,
  );
}
